import * as THREE from 'three';

type Surface = { mesh: THREE.Mesh; original: THREE.Material | THREE.Material[]; faded: THREE.Material | THREE.Material[]; opacity: number };

export class AntOcclusion {
  readonly outline: THREE.Group;
  private readonly surfaces: Surface[] = [];
  private readonly ray = new THREE.Raycaster();
  private readonly projected = new THREE.Vector3();
  private readonly target = new THREE.Vector3();
  private readonly pairs: [THREE.Object3D, THREE.Object3D][] = [];
  count = 0;
  constructor(world: THREE.Group, ant: THREE.Group, scene: THREE.Scene) {
    world.traverse(o => {
      if (!(o instanceof THREE.Mesh) || !o.layers.isEnabled(1)) return;
      const clone = (m: THREE.Material) => { const c = m.clone(); c.transparent = true; c.depthWrite = false; return c; };
      this.surfaces.push({ mesh: o, original: o.material, faded: Array.isArray(o.material) ? o.material.map(clone) : clone(o.material), opacity: 1 });
    });
    const rim = new THREE.ShaderMaterial({
      transparent: true, depthTest: false, depthWrite: false, side: THREE.BackSide,
      vertexShader: 'varying vec3 n; varying vec3 v; void main(){ vec4 p=modelViewMatrix*vec4(position,1.0); n=normalize(normalMatrix*normal); v=normalize(-p.xyz); gl_Position=projectionMatrix*p; }',
      fragmentShader: 'varying vec3 n; varying vec3 v; void main(){ float edge=1.0-abs(dot(normalize(n),normalize(v))); if(edge<0.65) discard; gl_FragColor=vec4(1.0,0.88,0.52,0.75); }',
    });
    this.outline = ant.clone(true); scene.add(this.outline);
    const originals: THREE.Object3D[] = []; ant.traverse(o => originals.push(o));
    let i = 0; this.outline.traverse(o => {
      this.pairs.push([originals[i++], o]); o.layers.set(3);
      if (o instanceof THREE.Mesh) { o.material = rim; o.castShadow = false; o.receiveShadow = false; o.renderOrder = 1000; }
    });
    this.outline.visible = false;
    this.ray.layers.set(1);
  }
  update(camera: THREE.Camera, position: THREE.Vector3, dt: number, active: boolean): void {
    const covered = new Set<THREE.Object3D>();
    if (active) {
      for (const [dx, dz] of [[0, 0], [.35, 0], [-.35, 0], [0, .35], [0, -.35]]) {
        this.target.copy(position).add(new THREE.Vector3(dx, .4, dz));
        this.projected.copy(this.target).project(camera);
        this.ray.setFromCamera(new THREE.Vector2(this.projected.x, this.projected.y), camera);
        this.ray.far = this.ray.ray.origin.distanceTo(this.target) - .15;
        for (const hit of this.ray.intersectObjects(this.surfaces.map(s => s.mesh), false)) covered.add(hit.object);
      }
    }
    this.count = covered.size;
    for (const s of this.surfaces) {
      s.opacity += ((covered.has(s.mesh) ? .28 : 1) - s.opacity) * (1 - Math.exp(-9 * dt));
    }
    for (const [original, clone] of this.pairs) {
      clone.position.copy(original.position); clone.quaternion.copy(original.quaternion); clone.scale.copy(original.scale);
      if (clone instanceof THREE.Mesh) clone.scale.multiplyScalar(1.08);
    }
    this.outline.visible = active && covered.size > 0;
  }
  apply(): void {
    for (const s of this.surfaces) {
      if (s.opacity > .995) continue;
      const materials = Array.isArray(s.faded) ? s.faded : [s.faded];
      materials.forEach((m, i) => { const originals = Array.isArray(s.original) ? s.original : [s.original]; m.opacity = originals[i].opacity * s.opacity; });
      s.mesh.material = s.faded;
    }
  }
  restore(): void { for (const s of this.surfaces) s.mesh.material = s.original; }
}
